import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Stepper, Step, StepLabel, Box, Typography,
  CircularProgress,
} from '@mui/material';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import CheckIcon from '@mui/icons-material/Check';
import EditIcon from '@mui/icons-material/Edit';
import { DatosRemitente, getErrorMessage } from './DatosRemitente';
import { DatosDestinatario } from './DatosDestinatario';
import { DatosProducto, isDatosProductoValido } from './DatosProducto';
import { ResumenEnvio } from './ResumenEnvio';
import {
  EnvioNacionalForm,
  ENVIO_NACIONAL_FORM_INITIAL,
  getEnvioNacionalFormInitial,
  DatosRemitente as DatosRemitenteType,
  DatosDestinatario as DatosDestinatarioType,
  DatosProducto as DatosProductoType,
  buildEnvioNacionalColombiaPayload,
} from './EnvioNacionalModal.types';
import {
  getProductoEnvioNacional,
  isPersonaValida,
  isPersonaValidaCO,
} from './EnvioNacionalModal.utils';
import { VentaApiService, TipoViaResponse, CiudadCOResponse } from '../../../infrastructure/api/VentaApiService';
import { useCart } from '../../../application/context/CartContext';
import { useSession } from '../../../../../core/context/SessionContext';
import { CiudadCO } from './PersonaFormColombia';

const PASOS = ['Remitente', 'Destinatario', 'Producto', 'Resumen'];

interface CustomStepIconProps {
  active?: boolean;
  completed?: boolean;
  icon: React.ReactNode;
}

const CustomStepIcon: React.FC<CustomStepIconProps> = ({ active, completed, icon }) => {
  if (completed) {
    return (
      <Box sx={{
        width: 28, height: 28, borderRadius: '50%',
        backgroundColor: 'primary.main',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <CheckIcon sx={{ color: '#fff', fontSize: 18 }} />
      </Box>
    );
  }
  if (active) {
    return (
      <Box sx={{
        width: 28, height: 28, borderRadius: '50%',
        backgroundColor: 'primary.main',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <EditIcon sx={{ color: '#fff', fontSize: 16 }} />
      </Box>
    );
  }
  return (
    <Box sx={{
      width: 28, height: 28, borderRadius: '50%',
      backgroundColor: '#e0e0e0',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <Typography variant="caption" sx={{ color: '#9e9e9e', fontWeight: 700 }}>{icon}</Typography>
    </Box>
  );
};

interface Props {
  open: boolean;
  onClose: () => void;
  accessToken: string;
  initialForm?: EnvioNacionalForm;
  editingItemId?: string;
  initialStep?: number;
  onAgregarAlCarrito?: () => void;
}

export const EnvioNacionalModal: React.FC<Props> = ({
  open, onClose, accessToken, initialForm, editingItemId, initialStep, onAgregarAlCarrito,
}) => {
  const { agregarAlCarrito, agregarGuiaColombia, editarEnvioNacional, editarGuiaColombia } = useCart();
  const { terminal } = useSession();
  const pais = terminal?.pais ?? '';
  const esColombia = pais === 'CO';

  const [pasoActual, setPasoActual] = useState(0);
  const [form, setForm] = useState<EnvioNacionalForm>(ENVIO_NACIONAL_FORM_INITIAL);
  const [editingId, setEditingId] = useState<string | undefined>(undefined);

  // México — código postal
  const [isLoadingMunicipioRemitente, setIsLoadingMunicipioRemitente] = useState(false);
  const [errorCPRemitente, setErrorCPRemitente] = useState<string | null>(null);
  const [isLoadingMunicipioDestinatario, setIsLoadingMunicipioDestinatario] = useState(false);
  const [errorCPDestinatario, setErrorCPDestinatario] = useState<string | null>(null);

  // Cotización
  const [precioCotizado, setPrecioCotizado] = useState<number | null>(null);
  const [isLoadingCotizacion, setIsLoadingCotizacion] = useState(false);
  const [errorCotizacion, setErrorCotizacion] = useState<string | null>(null);
  const cotizacionAbortRef = useRef<AbortController | null>(null);

  // Colombia — maestros
  const [tiposViaCO, setTiposViaCO] = useState<TipoViaResponse[]>([]);
  const [ciudadesCO, setCiudadesCO] = useState<CiudadCOResponse[]>([]);
  const [isLoadingCiudadesCO, setIsLoadingCiudadesCO] = useState(false);

  const dialogContentRef = useRef<HTMLDivElement>(null);

  // Carga inicial de maestros Colombia
  useEffect(() => {
    if (!open || !esColombia) return;
    setIsLoadingCiudadesCO(true);
    Promise.all([
      VentaApiService.obtenerTiposViaCO(accessToken),
      VentaApiService.obtenerCiudadesCO(accessToken),
    ])
      .then(([tipos, ciudades]) => {
        setTiposViaCO(tipos);
        setCiudadesCO(ciudades);
      })
      .catch(() => {})
      .finally(() => setIsLoadingCiudadesCO(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, esColombia]);

  useEffect(() => {
    if (open) {
      setPasoActual(initialStep ?? 0);
      setForm(initialForm ?? getEnvioNacionalFormInitial(pais));
      setEditingId(editingItemId);
      setErrorCPRemitente(null);
      setErrorCPDestinatario(null);
      setPrecioCotizado(null);
      setErrorCotizacion(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    const el = dialogContentRef.current;
    if (el?.scrollTo) el.scrollTo({ top: 0, behavior: 'smooth' });
  }, [pasoActual]);

  useEffect(() => {
    if (pasoActual !== 3) return;

    cotizacionAbortRef.current?.abort();
    const controller = new AbortController();
    cotizacionAbortRef.current = controller;

    setIsLoadingCotizacion(true);
    setPrecioCotizado(null);
    setErrorCotizacion(null);

    VentaApiService.cotizarEnvioNacional(form, accessToken, pais)
      .then(precio => {
        if (!controller.signal.aborted) setPrecioCotizado(precio);
      })
      .catch((error: unknown) => {
        if (!controller.signal.aborted) {
          const msg = error instanceof Error && error.message
            ? error.message
            : 'No se pudo calcular el precio. Intenta de nuevo.';
          setErrorCotizacion(msg);
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setIsLoadingCotizacion(false);
      });

    return () => controller.abort();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pasoActual]);

  const resetFormulario = () => {
    cotizacionAbortRef.current?.abort();
    setPasoActual(0);
    setForm(getEnvioNacionalFormInitial(pais));
    setErrorCPRemitente(null);
    setErrorCPDestinatario(null);
    setPrecioCotizado(null);
    setErrorCotizacion(null);
    setEditingId(undefined);
  };

  const handleClose = () => {
    resetFormulario();
    onClose();
  };

  // ── Handlers remitente ──────────────────────────────────────────────────

  const handleChangeRemitente = async (field: keyof DatosRemitenteType, value: string) => {
    if (field === 'codigoPostal' && !esColombia) {
      setForm(prev => ({ ...prev, remitente: { ...prev.remitente, codigoPostal: value, municipio: '' } }));
      setErrorCPRemitente(null);
      if (value.length === 5) {
        setIsLoadingMunicipioRemitente(true);
        try {
          const res = await VentaApiService.consultarCodigoPostal(value, accessToken, pais);
          setForm(prev => ({
            ...prev,
            remitente: { ...prev.remitente, municipio: res.ciudad, codigoCm: res.codigoCm, codigoCiudadDane: res.codigoCiudadDane },
          }));
        } catch {
          setErrorCPRemitente('Código postal no válido');
        } finally {
          setIsLoadingMunicipioRemitente(false);
        }
      }
      return;
    }
    setForm(prev => ({ ...prev, remitente: { ...prev.remitente, [field]: value } }));
  };

  const handleCiudadRemitenteSelect = (ciudad: CiudadCO) => {
    setForm(prev => ({
      ...prev,
      remitente: {
        ...prev.remitente,
        ciudadId: ciudad.id,
        ciudadLabel: ciudad.label,
        municipio: ciudad.label,
        codigoCm: ciudad.codigoCm,
      },
    }));
  };

  const handleDireccionRemitenteChange = (
    tipoVia: string, via: string, numeroVia: string, direccion: string,
  ) => {
    setForm(prev => ({
      ...prev,
      remitente: { ...prev.remitente, tipoVia, via, numeroVia, direccion },
    }));
  };

  // ── Handlers destinatario ───────────────────────────────────────────────

  const handleChangeDestinatario = async (field: keyof DatosDestinatarioType, value: string) => {
    if (field === 'codigoPostal' && !esColombia) {
      setForm(prev => ({ ...prev, destinatario: { ...prev.destinatario, codigoPostal: value, municipio: '' } }));
      setErrorCPDestinatario(null);
      if (value.length === 5) {
        setIsLoadingMunicipioDestinatario(true);
        try {
          const res = await VentaApiService.consultarCodigoPostal(value, accessToken, pais);
          setForm(prev => ({
            ...prev,
            destinatario: { ...prev.destinatario, municipio: res.ciudad, codigoCm: res.codigoCm, codigoCiudadDane: res.codigoCiudadDane },
          }));
        } catch {
          setErrorCPDestinatario('Código postal no válido');
        } finally {
          setIsLoadingMunicipioDestinatario(false);
        }
      }
      return;
    }
    setForm(prev => ({ ...prev, destinatario: { ...prev.destinatario, [field]: value } }));
  };

  const handleCiudadDestinatarioSelect = (ciudad: CiudadCO) => {
    setForm(prev => ({
      ...prev,
      destinatario: {
        ...prev.destinatario,
        ciudadId: ciudad.id,
        ciudadLabel: ciudad.label,
        municipio: ciudad.label,
        codigoCm: ciudad.codigoCm,
      },
    }));
  };

  const handleDireccionDestinatarioChange = (
    tipoVia: string, via: string, numeroVia: string, direccion: string,
  ) => {
    setForm(prev => ({
      ...prev,
      destinatario: { ...prev.destinatario, tipoVia, via, numeroVia, direccion },
    }));
  };

  // ── Handlers producto ───────────────────────────────────────────────────

  const handleChangeProducto = (field: keyof DatosProductoType, value: unknown) => {
    setForm(prev => ({ ...prev, producto: { ...prev.producto, [field]: value } }));
  };

  // ── Validaciones por paso ───────────────────────────────────────────────

  const isRemitenteValido = (): boolean => {
    if (esColombia) return isPersonaValidaCO(form.remitente, tiposViaCO);
    const r = form.remitente;
    const camposObligatorios: (keyof DatosRemitenteType)[] = [
      'nombre', 'numeroCelular', 'codigoPostal', 'municipio', 'colonia', 'direccion', 'correo',
    ];
    return camposObligatorios.every(
      field => r[field] !== '' && getErrorMessage(field, r, null) === ''
    );
  };

  const isDestinatarioValido = (): boolean => {
    if (esColombia) return isPersonaValidaCO(form.destinatario, tiposViaCO);
    return isPersonaValida(form.destinatario);
  };

  const isPasoActualValido = (): boolean => {
    if (pasoActual === 0) return isRemitenteValido();
    if (pasoActual === 1) return isDestinatarioValido();
    if (pasoActual === 2) return isDatosProductoValido(form.producto, esColombia);
    return true;
  };

  const handleSiguiente = () => setPasoActual(prev => prev + 1);
  const handleAtras = () => setPasoActual(prev => prev - 1);

  // ── Acción final — Agregar al carrito (MX) / Facturar (CO) ─────────────

  const handleAgregarAlCarrito = () => {
    if (precioCotizado === null || precioCotizado <= 0) return;
    const productoConPrecio = getProductoEnvioNacional(form.producto.tipo, precioCotizado);
    if (editingId) {
      editarEnvioNacional(editingId, form, productoConPrecio);
    } else {
      localStorage.setItem('ultimo_envio_nacional', JSON.stringify(form));
      agregarAlCarrito(productoConPrecio, form);
    }
    handleClose();
    onAgregarAlCarrito?.();
  };

  const handleAgregarGuiaColombia = () => {
    if (precioCotizado === null || precioCotizado <= 0) return;
    const precio = precioCotizado;
    const productoCO = {
      id: 'co-envio-nacional',
      codigo: 'ENV-CO-001',
      descripcion: 'Envío nacional Colombia',
      precioCOP: precio,
      precioMXN: precio,
      activo: true,
    };
    const payload = buildEnvioNacionalColombiaPayload(form, tiposViaCO);
    if (editingId) {
      editarGuiaColombia(editingId, payload, form, productoCO);
    } else {
      agregarGuiaColombia(productoCO, payload, form);
    }
    handleClose();
    onAgregarAlCarrito?.();
  };

  // ── Render por paso ─────────────────────────────────────────────────────

  const renderPaso = (): React.ReactNode => {
    if (pasoActual === 0) {
      return (
        <DatosRemitente
          key="remitente"
          datos={form.remitente}
          onChange={handleChangeRemitente}
          isLoadingMunicipio={isLoadingMunicipioRemitente}
          errorCodigoPostal={errorCPRemitente}
          pais={pais}
          ciudadesCO={ciudadesCO}
          tiposViaCO={tiposViaCO}
          isLoadingCiudadesCO={isLoadingCiudadesCO}
          onCiudadSelect={handleCiudadRemitenteSelect}
          onDireccionChange={handleDireccionRemitenteChange}
        />
      );
    }
    if (pasoActual === 1) {
      return (
        <DatosDestinatario
          key="destinatario"
          datos={form.destinatario}
          onChange={handleChangeDestinatario}
          isLoadingMunicipio={isLoadingMunicipioDestinatario}
          errorCodigoPostal={errorCPDestinatario}
          pais={pais}
          ciudadesCO={ciudadesCO}
          tiposViaCO={tiposViaCO}
          isLoadingCiudadesCO={isLoadingCiudadesCO}
          onCiudadSelect={handleCiudadDestinatarioSelect}
          onDireccionChange={handleDireccionDestinatarioChange}
        />
      );
    }
    if (pasoActual === 2) {
      return (
        <DatosProducto
          datos={form.producto}
          onChange={handleChangeProducto}
          esColombia={esColombia}
        />
      );
    }
    return (
      <Box>
        <ResumenEnvio
          form={form}
          onEditarPaso={setPasoActual}
          precioCotizado={precioCotizado}
          isLoadingCotizacion={isLoadingCotizacion}
          errorCotizacion={errorCotizacion}
          esColombia={esColombia}
        />
      </Box>
    );
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', borderBottom: '1px solid #e0e0e0' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LocalShippingIcon color="primary" />
          Creación de guía nacional
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={{ pt: 3 }} ref={dialogContentRef}>
        <Stepper activeStep={pasoActual} sx={{ mb: 3 }}>
          {PASOS.map(label => (
            <Step key={label}>
              <StepLabel slots={{ stepIcon: CustomStepIcon }}>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
        {renderPaso()}
      </DialogContent>

      <DialogActions sx={{ p: 2, backgroundColor: '#f9f9f9', gap: 1 }}>
        <Button onClick={handleClose} color="inherit" sx={{ fontWeight: 'bold' }}>
          Cancelar
        </Button>
        <Box sx={{ flexGrow: 1 }} />
        {pasoActual > 0 && pasoActual < PASOS.length - 1 && (
          <Button onClick={handleAtras} variant="outlined" sx={{ fontWeight: 'bold' }}>
            Atrás
          </Button>
        )}
        {pasoActual < PASOS.length - 1 ? (
          <Button
            onClick={handleSiguiente}
            variant="contained"
            disabled={!isPasoActualValido()}
            sx={{ fontWeight: 'bold', borderRadius: 8, px: 3 }}
          >
            Siguiente
          </Button>
        ) : (
          <Button
            onClick={esColombia ? handleAgregarGuiaColombia : handleAgregarAlCarrito}
            variant="contained"
            color={esColombia ? 'primary' : 'secondary'}
            disabled={isLoadingCotizacion || precioCotizado === null || precioCotizado <= 0}
            startIcon={
              isLoadingCotizacion
                ? <CircularProgress size={18} color="inherit" />
                : <AddShoppingCartIcon />
            }
            sx={{ fontWeight: 'bold', borderRadius: 8, px: 3 }}
          >
            {isLoadingCotizacion ? 'Calculando...' : 'Agregar al carrito'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
